import type { CSSProperties, FC, ReactNode } from 'react'
import type { ChartAnimation } from '@/components/charts/chart-utils'

import { cn } from '@/lib/utils'

export interface StackedSegment {
    name: string
    value: number
    color: string
}

interface StackedBarProps {
    title: string
    segments: Array<StackedSegment>
    legendCount?: number
    formatValue?: (value: number) => string
    animation?: ChartAnimation
    delay?: number
    surface?: 'light' | 'dark'
    className?: string
}

const defaultFormat = (value: number): string => value.toLocaleString('en-US')

/** A single 100% bar split into categories, with a compact legend. */
export const StackedBar: FC<StackedBarProps> = ({
    title,
    segments,
    legendCount = 3,
    formatValue = defaultFormat,
    animation = 'inview',
    delay = 0,
    surface = 'light',
    className
}): ReactNode => {
    const total = segments.reduce((sum, segment) => sum + segment.value, 0)
    const isDark = surface === 'dark'

    return (
        <figure
            className={cn(animation === 'autoplay' && 'chart-autoplay', className)}
            data-chart={animation === 'manual' ? undefined : ''}
            data-inview={animation === 'autoplay' ? 'true' : undefined}
        >
            <div aria-hidden='true' className='flex h-2 w-full gap-[2px] overflow-hidden rounded-full'>
                {segments.map((segment, index) => (
                    <span
                        key={segment.name}
                        data-segment=''
                        className={cn(
                            'chart-grow-x h-full',
                            index === 0 && 'rounded-l-full',
                            index === segments.length - 1 && 'rounded-r-full'
                        )}
                        style={{
                            width: `${total ? (segment.value / total) * 100 : 0}%`,
                            backgroundColor: segment.color,
                            '--chart-delay': `${delay + index * 80}ms`
                        } as CSSProperties}
                    />
                ))}
            </div>

            {legendCount > 0 && (
                <ul className='mt-3 grid gap-1.5'>
                    {segments.slice(0, legendCount).map(segment => (
                        <li key={segment.name} className='flex items-center gap-2 text-[11px]'>
                            <span aria-hidden='true' className='size-2 shrink-0 rounded-[2px]' style={{ backgroundColor: segment.color }} />
                            <span className={isDark ? 'text-cream/70' : 'text-stone-dark'}>{segment.name}</span>
                            <span className={cn('ml-auto tabular-nums', isDark ? 'text-cream/55' : 'text-stone')}>
                                {total ? Math.round((segment.value / total) * 100) : 0}%
                            </span>
                            <span className={cn('w-16 text-right font-medium tabular-nums', isDark ? 'text-cream' : 'text-burgundy')}>
                                {formatValue(segment.value)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            <figcaption className='sr-only'>
                {title}: {segments.map(segment => `${segment.name} ${formatValue(segment.value)}`).join(', ')}
            </figcaption>
        </figure>
    )
}
